import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Save, Upload, Eye, CheckCircle2, AlertCircle, Plus, X } from "lucide-react";
import { tplApi } from "@/lib/templateApi";

const SOCIAL_KEYS = [
  { key: "instagram", label: "Instagram" },
  { key: "facebook",  label: "Facebook" },
  { key: "tiktok",    label: "TikTok" },
  { key: "twitter",   label: "Twitter / X" },
  { key: "linkedin",  label: "LinkedIn" },
  { key: "youtube",   label: "YouTube" },
];

export default function FooterEditor() {
  const { tenant, slug } = useOutletContext();
  const theme = tenant?.theme || {};
  const primary = theme.primary_color || "#1E3A5F";

  const [form, setForm] = useState({
    copyright: "",
    links: [],
    social: { instagram: "", facebook: "", tiktok: "", twitter: "", linkedin: "", youtube: "" },
  });
  const [published, setPublished] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await tplApi.getAdminContent(slug);
      const m = res.modules?.footer;
      if (m) { const src = m.draft || m.published; if (src) setForm(src.content_json); if (m.published) setPublished(m.published.content_json); }
    } catch {}
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const flash = (msg, type = "success", link = null) => { setToast({ msg, type, link }); setTimeout(() => setToast(null), 5000); };
  const save = async (action) => {
    setSaving(true);
    try { await tplApi.saveContent(slug, "footer", form, action); flash(action === "publish" ? "Published!" : "Draft saved.", "success", action === "publish" ? `/t/${slug}` : null); if (action === "publish") setPublished({ ...form }); }
    catch (e) { flash(e.message, "error"); } finally { setSaving(false); }
  };

  const updateSocial = (key, val) => setForm((p) => ({ ...p, social: { ...p.social, [key]: val } }));
  const addLink = () => setForm((p) => ({ ...p, links: [...p.links, { label: "", url: "" }] }));
  const removeLink = (i) => setForm((p) => ({ ...p, links: p.links.filter((_, j) => j !== i) }));
  const updateLink = (i, field, val) => setForm((p) => {
    const links = [...p.links]; links[i] = { ...links[i], [field]: val }; return { ...p, links };
  });

  const activeSocials = Object.entries(form.social || {}).filter(([, v]) => v);

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="footer-editor">
      <div className="lg:w-[440px] shrink-0 border-r border-white/[0.06] overflow-auto">
        <div className="p-6 border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-white">Footer</h2>
          <p className="text-slate-500 text-xs mt-0.5">Copyright text, links, and social media profiles.</p>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-300 block mb-1">Copyright Text</label>
            <Input value={form.copyright} onChange={(e) => setForm((p) => ({ ...p, copyright: e.target.value }))}
              className="bg-[#1E293B] border-white/10 text-white h-10 rounded-lg focus:ring-orange-500" data-testid="footer-field-copyright" />
            <p className="text-[11px] text-slate-600 mt-1">e.g. "2026 Your Business. All rights reserved."</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">Footer Links</label>
              <button onClick={addLink} className="text-xs text-orange-500 hover:text-orange-400 flex items-center gap-1"><Plus size={12} /> Add Link</button>
            </div>
            {form.links.map((link, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={link.label} onChange={(e) => updateLink(i, "label", e.target.value)} placeholder="Label"
                  className="bg-[#1E293B] border-white/10 text-white h-9 rounded-lg text-xs flex-1" />
                <Input value={link.url} onChange={(e) => updateLink(i, "url", e.target.value)} placeholder="URL"
                  className="bg-[#1E293B] border-white/10 text-white h-9 rounded-lg text-xs flex-1" />
                <button onClick={() => removeLink(i)} className="text-slate-600 hover:text-red-400 shrink-0"><X size={14} /></button>
              </div>
            ))}
            <p className="text-[11px] text-slate-600">Links that appear in the footer (Privacy Policy, etc.).</p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-300 block mb-2">Social Media</label>
            <p className="text-[11px] text-slate-600 mb-3">Only filled-in profiles will show on the site.</p>
            {SOCIAL_KEYS.map((s) => (
              <div key={s.key} className="mb-2">
                <label className="text-xs text-slate-500 block mb-0.5">{s.label}</label>
                <Input value={(form.social || {})[s.key] || ""} onChange={(e) => updateSocial(s.key, e.target.value)} placeholder={`https://${s.key}.com/...`}
                  className="bg-[#1E293B] border-white/10 text-white h-9 rounded-lg text-xs" data-testid={`footer-social-${s.key}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="p-6 border-t border-white/[0.06] flex gap-3 sticky bottom-0 bg-[#0F172A]">
          <button onClick={() => save("draft")} disabled={saving} className="flex-1 bg-[#1E293B] border border-white/10 text-slate-300 hover:text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-draft-btn"><Save size={15} /> Save Draft</button>
          <button onClick={() => save("publish")} disabled={saving} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="publish-btn"><Upload size={15} /> Publish</button>
        </div>
      </div>

      <div className="flex-1 bg-[#0B1120] overflow-auto" data-testid="footer-preview-panel">
        <div className="p-4 border-b border-white/[0.06] flex items-center gap-2"><Eye size={14} className="text-slate-500" /><span className="text-xs text-slate-500 font-medium">Live Preview</span></div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-white/[0.06]" style={{ maxWidth: 800, background: primary }}>
            <div className="py-8 px-8">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <span className="font-bold text-white">{tenant?.name}</span>
                {form.links.length > 0 && (
                  <div className="flex gap-4">
                    {form.links.map((l, i) => l.label && <span key={i} className="text-white/50 text-xs hover:text-white/70">{l.label}</span>)}
                  </div>
                )}
              </div>
              {activeSocials.length > 0 && (
                <div className="flex gap-3 justify-center mt-4">
                  {activeSocials.map(([k]) => <span key={k} className="text-white/40 text-xs capitalize">{k}</span>)}
                </div>
              )}
              <p className="text-white/30 text-xs text-center mt-4">&copy; {form.copyright || `${new Date().getFullYear()} ${tenant?.name}`}</p>
            </div>
          </div>
        </div>
      </div>
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 text-sm font-medium z-50 ${toast.type === "error" ? "bg-red-900/90 text-red-200 border border-red-800/50" : "bg-emerald-900/90 text-emerald-200 border border-emerald-800/50"}`}>{toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}{toast.msg}{toast.link && <a href={toast.link} target="_blank" rel="noopener noreferrer" className="underline ml-1">View site &rarr;</a>}</div>}
    </div>
  );
}
